import { Box, Spinner, Text, focusOutline } from '@apia/theme';
import { IAllProps } from '@tinymce/tinymce-react';
import React from 'react';
import IProperty from '../../../../JSApi/constants/IProperty';
import useContext from '../../../../hooks/useContext';
import FieldContext from '../fieldsStore/FieldContext';
import { getFieldErrorStyles } from '../style';
import MakeField from '../fieldsStore/MakeField';
import useCombinedRefs from '../../../../hooks/useCombinedRefs';
import useApiaFieldCommonProps from './useApiaFieldCommonProps';
import { ApiaComponentContextPrimitive } from '../apiaComponent/ApiaComponentContext';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import globalFocus from '../../../../utils/globalFocus';
import { customEvents } from '../../../../utils/dom';
import { useApiaFormContext } from '../page/ApiaForm';

const Editor = React.lazy<React.FunctionComponent<IAllProps>>(() => {
  const component: Promise<{
    default: React.FunctionComponent<IAllProps>;
  }> | void = import(
    /* webpackChunkName: "apia-execution-LazyApiaEditor" */ './LazyApiaEditor'
  ).catch(console.error) as unknown as Promise<{
    default: React.FunctionComponent<IAllProps>;
  }>;

  return component;
});

const ApiaEditor = MakeField(
  React.forwardRef<HTMLTextAreaElement>((_, ref) => {
    const {
      setValue,
      fireEvent,
      state: { isValid, value },
      doFocus,
    } = useContext(FieldContext);
    const { fieldId } = useContext(ApiaComponentContextPrimitive);
    const props = usePropsSelector(fieldId);

    const { isReadonly, identifier } = useApiaFieldCommonProps({
      className: 'editor__field',
    });

    const handleEditorChange = (content: string): void => {
      if (isReadonly) return;
      setValue(content);
    };

    const [isFocused, setFocused] = React.useState(false);
    const handleEditorOnBlur = () => {
      fireEvent('onChange');
      setFocused(false);
    };

    const editorRef = React.useRef<HTMLTextAreaElement>(null);
    const assignRefs = useCombinedRefs<HTMLTextAreaElement>(
      editorRef,
      ref as unknown as React.ForwardedRef<unknown>,
    );
    doFocus(() => {
      editorRef.current?.focus();
      (
        (editorRef.current as unknown as Record<string, unknown>)
          ?.iframeElement as HTMLInputElement
      )?.scrollIntoView({
        block: 'center',
      });
    });

    const editorSx = React.useMemo(
      () => ({
        height: '100%',
        minHeight: '150px',
        ...getFieldErrorStyles(isValid),
        ...(isFocused ? { ...focusOutline } : undefined),
        '*': { zIndex: 0 },
        p: '2px',
        variant: 'layout.execution.form.fields.editor',
      }),
      [isFocused, isValid],
    );
    const langFile =
      window.LANG_CODE === 'pt'
        ? `${window.LANG_CODE}_${window.LANG_CODE.toUpperCase()}`
        : window.LANG_CODE;

    const SpinnerSuspense = React.useMemo(() => <Spinner />, []);
    const [boxRef, setRef] = React.useState<HTMLElement | null>(null);
    React.useEffect(() => {
      if (boxRef) {
        const handleCustomEvent = (ev: Event) => {
          ev.stopPropagation();
          editorRef.current?.focus();
        };
        boxRef.addEventListener(customEvents.focus, handleCustomEvent);
        return () =>
          boxRef.removeEventListener(customEvents.focus, handleCustomEvent);
      }
      return () => {};
    }, [boxRef, editorRef]);

    const { isFormReadOnly } = useApiaFormContext();

    return (
      <Box
        data-variant="layout.execution.form.fields.editor"
        sx={editorSx}
        data-required={props[IProperty.PROPERTY_REQUIRED]}
        ref={setRef}
        aria-description={props[IProperty.PROPERTY_ARIA_DESCRIPTION]}
        aria-label={props[IProperty.PROPERTY_ARIA_LABEL]}
        title={props[IProperty.PROPERTY_TITLE]}
        {...identifier}
      >
        {!(props[IProperty.PROPERTY_INPUT_AS_TEXT] || isFormReadOnly) && (
          <React.Suspense fallback={SpinnerSuspense}>
            <Editor
              key={`${fieldId}__editor}`}
              value={value === '' ? '<p></p>' : (value as string)}
              onInit={(_evt, editor) => {
                if (props.autoFocus === 'true') {
                  editor.focus();
                }
                assignRefs(editor as unknown as HTMLTextAreaElement);
              }}
              disabled={isReadonly || props[IProperty.PROPERTY_DISABLED]}
              init={{
                placeholder: props[IProperty.PROPERTY_PLACE_HOLDER],
                // skin: 'oxide',
                // content_css: false,
                a11y_advanced_options: true,
                body_class: fieldId,
                skin: false,
                id: fieldId,
                content_css: false,
                content_style:
                  '#tinymce {padding: 12px; color: #4a4a4a; font-family: sans-serif; font-size: 1em;}',
                language: langFile,
                language_url: `scripts/tinymce/jscripts/tinymce_5.6.2/langs/${langFile}.js`,
                height: '100%',
                branding: false,
                browser_spellcheck: true,
                contextmenu: false,
                relative_urls: false,
                remove_script_host: false,
                menubar:
                  isReadonly || props[IProperty.PROPERTY_DISABLED]
                    ? ''
                    : 'file edit view insert format tools table help',
                fontsize_formats: '8px 10px 12px 14px 16px 18px 24px 36px 48px',
                plugins:
                  isReadonly || props[IProperty.PROPERTY_DISABLED]
                    ? undefined
                    : [
                        'advlist autolink lists link image charmap print preview anchor',
                        'searchreplace visualblocks code fullscreen',
                        'insertdatetime media table paste code help wordcount',
                      ],
                toolbar:
                  isReadonly || props[IProperty.PROPERTY_DISABLED]
                    ? ''
                    : 'undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | forecolor backcolor casechange permanentpen formatpainter removeformat ',
              }}
              onKeyPress={(ev) => {
                if (isReadonly) ev.preventDefault();
              }}
              onBeforeExecCommand={(ev) => {
                if (isReadonly) ev.preventDefault();
              }}
              onEditorChange={handleEditorChange}
              onFocus={(ev) => {
                const activeElement = (ev.target as HTMLIFrameElement)
                  .contentDocument?.activeElement as HTMLElement;

                setFocused(true);
                globalFocus.focus = activeElement;
                activeElement.scrollIntoView({
                  inline: 'nearest',
                  block: 'nearest',
                });
              }}
              onBlur={handleEditorOnBlur}
            />
          </React.Suspense>
        )}
        {(props[IProperty.PROPERTY_INPUT_AS_TEXT] || isFormReadOnly) && (
          <Text dangerouslySetInnerHTML={{ __html: value as string }} />
        )}
      </Box>
    );
  }),
  {
    preSynchronize: ({
      fieldContext: {
        state: { value, lastValidValue },
      },
    }) => {
      return value === lastValidValue ? 'cancel' : true;
    },
    doSynchronize(
      {
        processFieldSubmit,
        fieldContext: {
          state: { value },
        },
      },
      additionalProps,
    ) {
      return processFieldSubmit({
        value,
        clearValues: true,
        ...additionalProps,
      });
    },
  },
);

export default ApiaEditor;
